import { Card, Flex } from 'antd';
import { CustomTableHeader } from '../custom-table-header/custom-table-header';
import styles from './documents-header.module.scss';
import PlusIcon from '@/assets/icons/plus.svg?react';
import Dropdown from '../form/dropdown/dropdown';
import SearchField from '../search-field/search-field';
import { useUser } from '@/hooks/use-user/use-user';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import { DocumentsFilters } from '@/services/documents-service/documents-service.types';
import { TYPE_OPTIONS } from './documents-header.constants';
import { CustomFilters } from '../custom-filters/custom-filters';
import { Button } from '../button/button';
import { useNavigate } from 'react-router';
import Icon from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface DocumentsHeaderProps {
  companiesList: string[];
  documentsNumber: number;
  filters?: DocumentsFilters;
  onChangeFilters: (filters: DocumentsFilters) => void;
  onSearchQueryChange: (query: string) => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
}

export function DocumentsHeader({
  filters,
  onChangeFilters,
  companiesList,
  documentsNumber,
  onSearchQueryChange,
  searchResults,
  isSearchLoading,
  searchError,
}: DocumentsHeaderProps) {
  const { t } = useTranslation(['documents', 'common']);
  const { isFetched } = useUser();
  const navigate = useNavigate();
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, onChangeFilters);

  const handleTypeChange = (value: string) => {
    onChangeFilters({ ...filters, type: value });
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex vertical className={styles.topHeaderContainer} gap={20}>
        <Flex gap={20} className={styles.documentsCountSectionWrapper}>
          <Flex gap={30} align="center" className={styles.documentsCountSectionContainer}>
            <CustomTableHeader
              textHeader1={t('documents:header.title')}
              count={documentsNumber}
            />
          </Flex>
          <Flex gap={20} className={styles.filtersSectionContainer} align="center">
            <Dropdown
              className={styles.dropdown}
              wrapperClassName={styles.wrapperClassName}
              dropdownListClass={styles.typeDropdownList}
              onChange={handleTypeChange}
              placeholder={t('documents:header.typePlaceholder')}
              options={TYPE_OPTIONS.map((value) => ({
                value,
                label: t(`documents:types.${value}`),
              }))}
              value={filters?.type}
            />

            {isFetched && (
              <CustomFilters
                customClassName={styles.customSelectClassName}
                companiesList={companiesList}
                filters={filters || {}}
                defaultCompanyValue={undefined}
                onChangeFilters={(updatedFilters) => {
                  onChangeFilters({
                    ...filters,
                    company_name: updatedFilters.company_name,
                  });
                }}
                onSearchQueryChange={onSearchQueryChange}
                searchResults={searchResults}
                isSearchLoading={isSearchLoading}
                searchError={searchError}
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      <Flex
        gap={20}
        className={styles.bottomHeaderContainer}
        wrap="wrap"
        justify="space-between"
        align="center"
      >
        <Flex>
          <Button buttonVariant="primary" onClick={() => navigate('/documents/new')}>
            <Icon component={() => <PlusIcon className={styles.plusIcon} />} />
            {t('documents:header.newDocument')}
          </Button>
        </Flex>
        <Flex vertical gap={5} className={styles.searchContainer}>
          <SearchField
            value={filterValue}
            onChange={onQueryChange}
            placeholder={t('common:actions.search')}
          />
        </Flex>
      </Flex>
    </Card>
  );
}
